import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDialogToggle } from '@/shared/hooks';
import { useDialogFormState } from './useDialogFormState';
import { useFormErrors } from './useFormErrors';
import type { DialogFormStateWithHandlers } from './useDialogFormState';

type LinkSelectFormValues = { linkId: string };
type LinkSelectErrors = ReturnType<typeof useFormErrors<LinkSelectFormValues>>;

type LinkSelectDialogState = {
  dialog: ReturnType<typeof useDialogToggle>;
  form: DialogFormStateWithHandlers<LinkSelectFormValues>;
  errors: LinkSelectErrors;
  saving: boolean;
  setSaving: (saving: boolean) => void;
  openDialog: (clearError?: () => void) => void;
  closeDialog: (clearError?: () => void) => void;
};

export const useLinkSelectDialogState = (): LinkSelectDialogState => {
  const dialog = useDialogToggle();
  const initialFormValues = useMemo(() => ({ linkId: '' }), []);
  const form = useDialogFormState(initialFormValues);
  const errors = useFormErrors(form);
  const [saving, setSaving] = useState(false);
  const { open, openDialog: openDialogInternal, closeDialog: closeDialogInternal } = dialog;
  const { resetDialog, handleClose } = form;

  useEffect(() => {
    if (open) return;
    setSaving(false);
    resetDialog();
  }, [open, resetDialog]);

  const openDialog = useCallback(
    (clearError?: () => void) => {
      clearError?.();
      resetDialog();
      openDialogInternal();
    },
    [openDialogInternal, resetDialog],
  );

  const closeDialog = useCallback(
    (clearError?: () => void) => {
      handleClose(closeDialogInternal, { clearError });
    },
    [closeDialogInternal, handleClose],
  );

  return { dialog, form, errors, saving, setSaving, openDialog, closeDialog };
};
